import { TableInputChoices } from "@tellescope/types-models";
import { DatePicker } from "@tellescope/ui";
import { Select, Input } from "@tellescope/design-system";
import { DatabaseSelectField } from "../DatabaseSelectField/DatabaseSelectField";
import { formatDate } from "@tellescope/utils";
import { parseDatabaseRecordResponse } from "../DatabaseSelectField";
import { dateFromOffsetMs, parseDateStringAnswer, toDateStringAnswer } from "../../utils";
import { useTranslation } from "react-i18next";

// Radix Select rejects an empty-string item value, so the clear option carries a sentinel that is
// mapped back to "" on the way out.
const CLEAR_SELECT_VALUE = "__tellescope_input_table_none__";

export type InputTableCellProps<T extends keyof TableInputChoices> = TableInputChoices[T] & {
  id: string;
  value: string;
  onChange: (value: string) => void;
  autoFocus?: boolean;
  /** The parent Input Table field's date bounds, applied to `Date` columns. */
  minDateOffsetMs?: number;
  maxDateOffsetMs?: number;
};

export const InputTableCell = <T extends keyof TableInputChoices>({
  id,
  type,
  autoFocus,
  minDateOffsetMs,
  maxDateOffsetMs,
  ...rest
}: InputTableCellProps<T>) => {
  const { t } = useTranslation("forms");

  switch (type) {
    case "Database": {
      const { value, onChange, info } = rest as Omit<InputTableCellProps<"Database">, "type">;
      if (!info.databaseId || !info.databaseLabel) return null;

      return (
        <DatabaseSelectField
          value={parseDatabaseRecordResponse(value)}
          id={id}
          onChange={(newVal) => onChange(JSON.stringify(newVal?.slice(0, 1) ?? []))}
          options={{ databaseId: info.databaseId, databaseLabel: info.databaseLabel, radio: true }}
          autoFocus={autoFocus}
        />
      );
    }
    case "Text": {
      const { value, onChange, label } = rest as Omit<InputTableCellProps<"Text">, "type">;
      return (
        <Input value={value} onChange={(e) => onChange(e.target.value)} placeholder={label} autoFocus={autoFocus} />
      );
    }
    case "Select": {
      const { value, onChange, label, info } = rest as Omit<InputTableCellProps<"Select">, "type">;
      return (
        <Select
          value={value || CLEAR_SELECT_VALUE}
          onValueChange={(newValue) => onChange(newValue === CLEAR_SELECT_VALUE ? "" : newValue)}
        >
          <Select.Trigger autoFocus={autoFocus}>{value || label}</Select.Trigger>
          <Select.Content>
            <Select.Item value={CLEAR_SELECT_VALUE}>{t("None")}</Select.Item>
            {info.choices.map((choice) => (
              <Select.Item key={choice} value={choice}>
                {choice}
              </Select.Item>
            ))}
          </Select.Content>
        </Select>
      );
    }
    case "Date": {
      const { value, onChange } = rest as Omit<InputTableCellProps<"Date">, "type">;
      const parsed = value ? parseDateStringAnswer(value) : null;
      const minDate = minDateOffsetMs !== undefined ? dateFromOffsetMs(minDateOffsetMs) : undefined;
      const maxDate = maxDateOffsetMs !== undefined ? dateFromOffsetMs(maxDateOffsetMs) : undefined;
      const disabledDates = [...(minDate ? [{ before: minDate }] : []), ...(maxDate ? [{ after: maxDate }] : [])];

      return (
        <DatePicker
          value={parsed ? new Date(parsed.year, parsed.month - 1, parsed.day) : undefined}
          onChange={(value) => onChange(value ? toDateStringAnswer(value) : "")}
          placeholder={formatDate(new Date(), "MM/DD/YYYY")}
          label=""
          displayFormat="MM/DD/YYYY"
          disabledDates={disabledDates.length > 0 ? disabledDates : undefined}
          inputProps={{ autoFocus }}
        />
      );
    }
  }
};
